import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, Save } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const blank = {
  name: "",
  license_no: "",
  address: "",
  footer_code: "",
  department_text: "QUALITY CONTROL DEPARTMENT",
  act_text: "THE DRUG & COSMETIC ACT. 1940 & THE RULES THEREUNDER",
  default_prepared_by: "",
  default_prepared_role: "",
  default_checked_by: "",
  default_checked_role: "",
  default_approved_by: "",
  default_approved_role: "",
  default_conclusion: "",
  logo_data_url: "",
};

export default function CompanyManager({ companies, onRefresh, token }) {
  const [editing, setEditing] = useState(null); // null | {...}
  const headers = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };

  const startNew = () => setEditing({ ...blank });
  const startEdit = (c) => setEditing({ ...blank, ...c });

  const handleLogo = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 250 * 1024) {
      toast.error("Logo must be under 250 KB");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setEditing((prev) => ({ ...prev, logo_data_url: reader.result }));
    reader.readAsDataURL(file);
  };

  const save = async () => {
    if (!editing.name?.trim()) { toast.error("Company name is required"); return; }
    const isEdit = !!editing.id;
    const url = isEdit ? `${API}/api/documents/companies/${editing.id}` : `${API}/api/documents/companies`;
    const r = await fetch(url, {
      method: isEdit ? "PUT" : "POST",
      headers,
      body: JSON.stringify(editing),
    });
    if (r.ok) {
      toast.success(isEdit ? "Updated" : "Created");
      setEditing(null);
      onRefresh();
    } else {
      const t = await r.text();
      toast.error("Save failed: " + t);
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete company "${c.name}"?`)) return;
    const r = await fetch(`${API}/api/documents/companies/${c.id}`, { method: "DELETE", headers });
    if (r.ok) { toast.success("Deleted"); onRefresh(); }
    else { toast.error("Delete failed"); }
  };

  if (editing) {
    return (
      <div className="p-4 sm:p-6 space-y-4" data-testid="company-editor">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold">{editing.id ? "Edit Company" : "Add New Company"}</h3>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => setEditing(null)} data-testid="company-cancel-btn">
              <X className="w-4 h-4 mr-1" /> Cancel
            </Button>
            <Button size="sm" onClick={save} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="company-save-btn">
              <Save className="w-4 h-4 mr-1" /> Save
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Company Name *" required>
            <Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="company-name-input" />
          </Field>
          <Field label="Mfg. Lic. No (auto-fills on COA)">
            <Input value={editing.license_no} onChange={(e) => setEditing({ ...editing, license_no: e.target.value })} placeholder="G/25A/5141-A" data-testid="company-license-input" />
          </Field>

          <Field label="Address">
            <Textarea value={editing.address} onChange={(e) => setEditing({ ...editing, address: e.target.value })} rows={2} />
          </Field>
          <Field label="Footer Code (e.g. FGANLCERTQA)">
            <Input value={editing.footer_code} onChange={(e) => setEditing({ ...editing, footer_code: e.target.value })} />
          </Field>

          <Field label="Department Text">
            <Input value={editing.department_text} onChange={(e) => setEditing({ ...editing, department_text: e.target.value })} />
          </Field>
          <Field label="Act / Regulation Text">
            <Input value={editing.act_text} onChange={(e) => setEditing({ ...editing, act_text: e.target.value })} />
          </Field>

          <Field label="Logo">
            <div className="flex items-center gap-2">
              <input type="file" accept="image/*" onChange={handleLogo} className="text-xs" data-testid="company-logo-input" />
              {editing.logo_data_url && (
                <>
                  <img src={editing.logo_data_url} alt="logo" className="h-10 w-10 object-contain border rounded" />
                  <Button size="sm" variant="ghost" onClick={() => setEditing({ ...editing, logo_data_url: "" })}>
                    <X className="w-3 h-3" />
                  </Button>
                </>
              )}
            </div>
          </Field>
          <Field label="Default Conclusion">
            <Input value={editing.default_conclusion} onChange={(e) => setEditing({ ...editing, default_conclusion: e.target.value })} placeholder="The above sample complies as per USP" />
          </Field>

          <div className="sm:col-span-2 mt-2 pt-3 border-t">
            <p className="text-xs font-semibold mb-2 text-muted-foreground uppercase tracking-wide">Default signatories (used on every COA)</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Prepared By — Name"><Input value={editing.default_prepared_by} onChange={(e) => setEditing({ ...editing, default_prepared_by: e.target.value })} /></Field>
              <Field label="Prepared By — Role"><Input value={editing.default_prepared_role} onChange={(e) => setEditing({ ...editing, default_prepared_role: e.target.value })} placeholder="TRAINEE OFFICER" /></Field>
              <Field label="Checked By — Name"><Input value={editing.default_checked_by} onChange={(e) => setEditing({ ...editing, default_checked_by: e.target.value })} /></Field>
              <Field label="Checked By — Role"><Input value={editing.default_checked_role} onChange={(e) => setEditing({ ...editing, default_checked_role: e.target.value })} placeholder="EXECUTIVE" /></Field>
              <Field label="Approved By — Name"><Input value={editing.default_approved_by} onChange={(e) => setEditing({ ...editing, default_approved_by: e.target.value })} /></Field>
              <Field label="Approved By — Role"><Input value={editing.default_approved_role} onChange={(e) => setEditing({ ...editing, default_approved_role: e.target.value })} placeholder="QUALITY HEAD" /></Field>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-xs text-muted-foreground">Companies are auto-loaded in every COA&apos;s dropdown.</p>
        <Button size="sm" onClick={startNew} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="add-company-btn">
          <Plus className="w-4 h-4 mr-1" /> Add Company
        </Button>
      </div>
      {companies.length === 0 ? (
        <Card><CardContent className="p-6 text-center text-sm text-muted-foreground">
          No companies yet. Click <b>Add Company</b> to create one.
        </CardContent></Card>
      ) : (
        <div className="space-y-2">
          {companies.map((c) => (
            <div key={c.id} className="flex items-center gap-2 p-2 sm:p-3 border rounded-lg hover:bg-muted/30" data-testid={`company-row-${c.id}`}>
              {c.logo_data_url ? (
                <img src={c.logo_data_url} alt="" className="h-10 w-10 object-contain border rounded flex-shrink-0" />
              ) : (
                <div className="h-10 w-10 rounded bg-indigo-100 dark:bg-indigo-950 flex items-center justify-center text-indigo-600 font-bold flex-shrink-0">
                  {c.name?.slice(0, 1).toUpperCase()}
                </div>
              )}
              <div className="min-w-0 flex-1">
                <p className="font-medium truncate">{c.name}</p>
                <p className="text-xs text-muted-foreground truncate font-mono">Lic: {c.license_no || "—"}</p>
              </div>
              <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => startEdit(c)} data-testid={`edit-company-${c.id}`}>
                <Pencil className="w-3.5 h-3.5" />
              </Button>
              <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-rose-600" onClick={() => remove(c)} data-testid={`delete-company-${c.id}`}>
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const Field = ({ label, children }) => (
  <div className="space-y-1">
    <Label className="text-xs">{label}</Label>
    {children}
  </div>
);
